<template>
  <div class="p-6">
    <h1 class="text-2xl font-bold text-gray-900 mb-4">Equipment Usage Logs</h1>

    <UsageList
      @create="openCreate"
      @view="openView"
      @edit="openEdit"
      @delete="confirmDelete"
      @open-fuel-prices="showPriceModal = true"
    />

    <!-- Add/Edit Usage Modal -->
    <UsageLogForm
      v-if="showForm"
      :is-open="showForm"
      :initial="editingItem"
      :loading="formLoading"
      @save="onSave"
      @cancel="closeForm"
    />

    <!-- Usage Detail Drawer -->
    <div v-if="showDetail">
      <div class="drawer-backdrop fixed inset-0 bg-black/40 z-40" @click="closeDetail"></div>
      <div
        class="drawer fixed right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl p-6 overflow-auto z-50 transition-all"
      >
        <div class="flex justify-between items-center mb-4 pb-2 border-b border-gray-100">
          <h3 class="text-lg font-semibold text-gray-800">Usage Details</h3>
          <button
            @click="closeDetail"
            class="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition"
          >
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <UsageDetail
          v-if="selectedLog"
          :log="selectedLog"
          @edit="openEditFromDetail"
          @delete="confirmDelete"
        />
      </div>
    </div>

    <!-- Confirm Delete Dialog -->
    <ConfirmDialog
      v-if="confirming"
      :title="confirmTitle"
      :message="confirmMessage"
      @confirm="doDelete"
      @cancel="cancelDelete"
    />

    <!-- Fuel Price Modal -->
    <FuelPriceModal
      :is-open="showPriceModal"
      @close="showPriceModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import UsageList from '@/components/usage/UsageList.vue'
import UsageDetail from '@/components/usage/UsageDetail.vue'
import UsageLogForm from '@/components/usage/UsageLogForm.vue'
import FuelPriceModal from '@/components/fuel/FuelPriceModal.vue'
import { useUsageStore } from '@/stores/usageStore'
import { useNotificationStore } from '@/stores/notification'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import type { UsageLog, UsageLogCreate } from '@/types/usage'

const usageStore = useUsageStore()
const notificationStore = useNotificationStore()

const showPriceModal = ref(false)
const showForm = ref(false)
const showDetail = ref(false)
const editing = ref(false)
const selectedId = ref<number | null>(null)
const confirming = ref(false)
const confirmTarget = ref<number | null>(null)
const confirmTitle = ref('Delete usage log')
const confirmMessage = ref('Are you sure you want to delete this usage log?')
const editingItem = ref<UsageLog | null>(null)
const formLoading = ref(false)

onMounted(async () => {
  await usageStore.fetchUsageLogs()
})

const selectedLog = computed(() => {
  if (!selectedId.value) return null
  return usageStore.usageLogs.find((l) => l.id === selectedId.value) || null
})

function openCreate() {
  editing.value = false
  editingItem.value = null
  showForm.value = true
}

function openEdit(id: number) {
  const item = usageStore.usageLogs.find((l) => l.id === id)
  if (item) {
    editing.value = true
    editingItem.value = { ...item }
    showForm.value = true
  }
}

function openEditFromDetail(id: number) {
  showDetail.value = false
  openEdit(id)
}

function openView(id: number) {
  selectedId.value = id
  showDetail.value = true
}

function closeForm() {
  showForm.value = false
}

function closeDetail() {
  showDetail.value = false
}

async function onSave(payload: UsageLogCreate) {
  formLoading.value = true
  try {
    if (editing.value && editingItem.value?.id) {
      await usageStore.updateUsageLog(editingItem.value.id, payload)
      notificationStore.showSuccess('Equipment usage log updated successfully!')
    } else {
      await usageStore.createUsageLog(payload)
      notificationStore.showSuccess('Equipment usage log recorded successfully!')
    }
    showForm.value = false
    await usageStore.fetchUsageLogs()
  } catch (err: unknown) {
    const errorMsg = (err as Error)?.message || 'Failed to save usage log.'
    notificationStore.showError(errorMsg)
  } finally {
    formLoading.value = false
  }
}

function confirmDelete(id: number) {
  confirmTarget.value = id
  confirming.value = true
}

async function doDelete() {
  if (!confirmTarget.value) return
  try {
    await usageStore.deleteUsageLog(confirmTarget.value)
    notificationStore.showSuccess('Usage log deleted successfully.')
    await usageStore.fetchUsageLogs()
    if (selectedId.value === confirmTarget.value) selectedId.value = null
  } catch (err: unknown) {
    const errorMsg = (err as Error)?.message || 'Failed to delete usage log.'
    notificationStore.showError(errorMsg)
  } finally {
    confirming.value = false
    confirmTarget.value = null
  }
}

function cancelDelete() {
  confirming.value = false
  confirmTarget.value = null
}
</script>
